/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {LicenseManager} from 'ag-grid-enterprise';
import {error} from '@ion/core/src/logger/logger';
import {GenericObject} from '@ion/core/src/types/entities';

/**
 * Provides a utility which initializes the underlying Ag Grid registration process.
 *
 * This function is to be invoked prior to the rendering of Ag Grid based components,
 * such as the `IONGrid`, etc..
 *
 * By default, the CalAmp Ag Grid license value will be set to that of the value of
 * the applications `NEXT_PUBLIC_AG_GRID_LICENSE` environment variable (as provided
 * via the applications `next.config.js` configuration), otherwise, for backwards
 * compatibility, the license value will fallback to using the `agGridLicense` entry
 * from the provided `environment.json` configuration file (via the function args).
 *
 * ```typescript
 *   import {registerGrid} from '@ion/dls/src/grid/IonGridLicense';
 *
 *   // invoke prior to initialization ...
 *   registerGrid();
 *
 *   ...
 *   // deprecated implementation (i.e. assign via environment json entry) ...
 *   loadConfig(config => {
 *     registerGrid(config);
 *   });
 * ```
 * @param An optional object (most typically, the contents of the `environment.json`
 *        document) which provides the agGridLicense key via an `agGridLicense` entry.
 *        Note, as per the AG Grid Upgrades as part of IMO-130 & IMO-112, setting the
 *        license key via environment.json is deprecated and this argument is only
 *        supported to provide backwards compatibility. Even if provided, the value
 *        will only be used if the `NEXT_PUBLIC_AG_GRID_LICENSE` environment variable
 *        is not set.
 */
export const registerGrid = ({agGridLicense}: GenericObject = {}) => {
  const license = process.env.NEXT_PUBLIC_AG_GRID_LICENSE || agGridLicense;
  if (license) {
    LicenseManager.setLicenseKey(license);
  } else {
    error('Ag Grid license Key has not been Provided by the NEXT_PUBLIC_AG_GRID_LICENSE default or the `agGridLicense` configuration entry');
  }
};
